import Highlight from "@/components/home/Highlight";
import ContactLink from "@/components/ContactLink";
import { Head, Link, usePage } from "@inertiajs/react";
import { ArrowRight, Check, Heart, CreditCard, Wifi, Battery } from "lucide-react";
import { Button } from "@/components/ui/button";
import FeeComparison from "@/components/marketing/FeeComparison";
import PricingTiers from "@/components/marketing/PricingTiers";
import { Badge } from "@/components/ui/badge";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import PageContainer from "@/components/marketing/PageContainer";
import SectionHeading from "@/components/marketing/SectionHeading";
import { formatMoney } from "@/lib/format";

export default function Pricing({ price_list }) {
  const { routes } = usePage().props;
  const startingPriceCents = Math.min(...price_list.map((tier) => tier.price_cents));

  return (
    <MarketingLayout>
      <Head title="Pricing" />

      {/* ── Hero ────────────────────────────────────────────── */}
      <PageContainer width="narrow" className="text-center space-y-6 pt-10 md:pt-14">
        <Badge className="uppercase tracking-wider">
          Early adopter pricing
        </Badge>
        <h1 className="text-4xl md:text-6xl leading-tight text-foreground">
          A flat <Highlight color="amber">campaign fee.</Highlight> No PagePledge transaction fees.
        </h1>
        <p className="text-muted-foreground max-w-xl mx-auto leading-relaxed">
          Campaigns start at {formatMoney(startingPriceCents)}, with pricing tiers based on the number
          of students in your campaign. Prices include an early adopter discount.
          Start with a free trial to set up your campaign and see how it works. Running it costs the flat campaign fee, paid before it goes live. PagePledge charges no transaction fees. Your payment
          provider charges its own processing fees, which donors can choose
          to cover at checkout.
        </p>
        <div className="flex flex-wrap gap-3 justify-center pt-2">
          <Button size="lg" asChild>
            <Link href={routes.new_school_application}>
              Sign up your school
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <ContactLink>Ask about pricing</ContactLink>
          </Button>
        </div>
      </PageContainer>

      <PageContainer width="wide" id="campaign-pricing" className="pt-4 pb-8 md:pb-10">
        <PricingTiers priceList={price_list} />
      </PageContainer>

      <PageContainer width="wide" id="processing-fees" className="pt-10 md:pt-12 pb-0">
        <div className="grid gap-8 lg:gap-12 md:grid-cols-2 max-w-5xl mx-auto items-center">
          <div className="space-y-5">
            <h2 className="text-3xl md:text-4xl leading-tight">Your payment account, your donations.</h2>
            <p className="text-muted-foreground leading-relaxed">
              Connect your school’s Square or Stripe account to receive donations.
              Square or Stripe charges its own card processing fees. PagePledge
              adds no transaction fees and charges only the flat campaign fee.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              At checkout, donors can choose to cover the processing fees so
              their full donation can reach your school. If they don’t, the
              provider deducts its fees from the donation.
            </p>
            <div className="border-l-2 border-accent/40 pl-5 space-y-2">
              <h3 className="font-semibold">Don’t have a payment account yet?</h3>
              <p className="text-muted-foreground leading-relaxed">
                Square has the lower published standard rate for Canadian online
                card payments: 2.8% + 30¢, compared with Stripe’s 2.9% + CA$0.30.
                Your account pricing and international cards can change the cost.
                Check the current rates at{" "}
                <a href="https://squareup.com/ca/en/payments/our-fees" className="underline underline-offset-4 hover:text-foreground">Square</a>
                {" "}and{" "}
                <a href="https://stripe.com/en-ca/pricing" className="underline underline-offset-4 hover:text-foreground">Stripe</a>
                {" "}before choosing an account.
              </p>
            </div>
          </div>
          <DonationFormPreview />
        </div>
      </PageContainer>

      <PageContainer width="wide" className="py-10 md:py-12">
        <div className="grid gap-5 md:grid-cols-[auto_1fr] md:gap-10 items-center max-w-4xl mx-auto border-y border-accent/20 py-8">
          <div className="text-accent">
            <p className="font-display text-6xl md:text-7xl">50%+</p>
            <p className="mt-1 text-sm font-medium">less administration time</p>
          </div>
          <div className="space-y-3">
            <h2 className="text-2xl md:text-3xl">Give your volunteers some time back.</h2>
            <p className="text-muted-foreground leading-relaxed">
              In our experience, volunteers spend at least 50% less time
              administering the campaign with PagePledge than with the
              platforms we used before. That’s time back for the people
              who make the fundraiser happen.
            </p>
          </div>
        </div>
      </PageContainer>

      <PageContainer width="wide" id="fee-comparison" className="py-10 md:py-12" outerClassName="bg-accent/5 border-y border-accent/15">
        <FeeComparison startingPriceCents={startingPriceCents} />
      </PageContainer>

      {/* ── CTA ─────────────────────────────────────────────── */}
      <PageContainer width="narrow" className="text-center space-y-6 pb-12 md:pb-16">
        <SectionHeading
          flourish="ready to try it?"
          title="Get early adopter pricing."
          subhead="Get an early adopter discount on your campaign, with hands-on onboarding support. Start with a free trial, and pay your flat fee when you're ready to run your campaign."
        />
        <div className="flex flex-wrap gap-3 justify-center">
          <Button size="lg" asChild>
            <Link href={routes.new_school_application}>
              Sign up your school
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <ContactLink>Ask us about pricing</ContactLink>
          </Button>
        </div>
      </PageContainer>
    </MarketingLayout>
  );
}

function DonationFormPreview() {
  return (
    <figure className="w-full max-w-[360px] mx-auto space-y-4">
      <div role="img" aria-label="Example donation form: a 25 dollar donation with the optional 1 dollar and 3 cent Square processing fee checked, for a total of 26 dollars and 3 cents. The school receives the full 25 dollars." className="rounded-[2.75rem] border-[8px] border-stone-900 bg-background shadow-xl overflow-hidden select-none">
        <div className="flex items-center justify-between px-5 pt-3 pb-2 text-[10px] font-semibold" aria-hidden="true">
          <span>9:41</span><span className="h-5 w-20 rounded-full bg-stone-900" /><span className="flex gap-1"><Wifi className="h-3 w-3" /><Battery className="h-3 w-3" /></span>
        </div>
        <div className="border-b border-border pb-2 text-center text-[10px] text-muted-foreground">pagepledge.com</div>
        <div className="p-3">
        <div className="rounded-xl border border-primary/25 bg-primary/5 p-3">
        <div className="flex items-center gap-2">
          <Heart className="h-5 w-5 text-primary" />
          <p className="font-display text-xl font-semibold">Make a donation</p>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Support Lincoln Elementary’s read-a-thon.</p>
        <div className="mt-5 space-y-4">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Amount (CAD)</p>
            <div className="grid grid-cols-3 gap-2">
              {["$5", "$10", "$25", "$50", "$100", "Custom"].map((amount) => (
                <span key={amount} className={`rounded-lg border py-2 text-center text-sm font-semibold ${amount === "$25" ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>{amount}</span>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium">Your name</p>
            <div className="rounded-md border border-input bg-card px-3 py-2 text-sm text-muted-foreground">Grandma Jean</div>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium">Your email</p>
            <div className="rounded-md border border-input bg-card px-3 py-2 text-sm text-muted-foreground">jean@example.com</div>
          </div>
          <div className="space-y-1">
            <p className="text-xs font-medium">Card details</p>
            <div className="flex items-center gap-2 rounded-md border border-input bg-card px-3 py-2 text-sm text-muted-foreground">
              <CreditCard className="h-4 w-4 shrink-0" />
              <span className="text-xs tracking-wide">•••• •••• •••• 4242</span>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-lg border-2 border-accent/45 bg-accent/10 p-3">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded bg-accent text-accent-foreground"><Check className="h-3 w-3" strokeWidth={3} /></span>
            <p className="text-sm leading-relaxed">
              Cover the <strong>$1.03</strong> Square processing fee so Lincoln Elementary gets the full <strong>$25</strong>.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border border-input bg-card"><Check className="h-3 w-3" /></span>
            I agree to the privacy policy.
          </div>
          <div className="flex justify-between gap-3 rounded-lg border border-primary/20 bg-card p-3 text-sm">
            <span className="text-muted-foreground">Total charged</span>
            <span className="font-semibold tabular-nums">$26.03</span>
          </div>
          <div className="rounded-lg bg-primary py-3 text-center text-sm font-semibold text-primary-foreground">Donate $26.03</div>
        </div>
        </div>
        </div>
        <div className="mx-auto mb-2 h-1 w-24 rounded-full bg-stone-900" aria-hidden="true" />
      </div>
      <figcaption className="text-xs text-center leading-relaxed text-muted-foreground">
        Example checkout using Square’s standard Canadian online rate.
        Covering the fee is optional. Your provider’s rates may vary.
      </figcaption>
    </figure>
  );
}
